import React from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { SxProps, Theme } from '@mui/material/styles';

import Delete from '../../../images/delete.svg?react';
import RecordRow from './RecordRow';
import RecordRowSkeleton from './RecordRowSkeleton';

// Shared by the header, the skeleton rows and the empty state's reserved space.
const ROW_HEIGHT = 60;

function RecordsTable({
    tKey,
    paginatedRecords,
    selected,
    onSelectAll,
    isSelected,
    onRowClick,
    onDelete,
    isMobile,
    mobileDrawerVariant = 'records',
    onRowEdit,
    ro = false,
    loading = false,
    labelsById = {},
    onLabelMetaUpdated = (..._args: any[]) => {},
    emptyContent = null,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const isAllSelected = paginatedRecords.length > 0 && selected.length === paginatedRecords.length;

    // A finished fetch that found nothing used to render an empty <TableBody>, so
    // the table collapsed to its header and the pagination under it jumped up the
    // page. The empty state holds exactly the height the skeletons held, which is
    // also a row's height times the number of them, so nothing moves between
    // loading and settled.
    const skeletonRowCount = Math.max(5, paginatedRecords.length || 0);
    const reservedBodyHeight = skeletonRowCount * ROW_HEIGHT;
    const isEmpty = !loading && paginatedRecords.length === 0;
    // checkbox, number, status, device, time, trash — plus duration, note and
    // labels when there is room for them.
    const columnCount = isMobile ? 6 : 10;
    const numberLabel = t('Id');
    // Mobile takes its own labels for the two widest columns. The full ones
    // don't fit the narrow shares there, and a header that wraps drags the whole
    // row taller and leaves the rest of the labels on a ragged baseline.
    const deviceLabel = isMobile ? t('deviceIdShort') : t('deviceId');
    const addedTimeLabel = isMobile ? t('recordTimeShort') : t('recordTime');

    // Fixed on both, so the columns are the same width on /uploaded and /pending.
    // Desktop used to be `auto`, which sizes each column to whatever that page's
    // rows happen to contain — the two pages carry different data, so every
    // column landed on a different x and the two tables never lined up.
    const tableSx: SxProps<Theme> = isMobile
        ? {
              width: '100%',
              tableLayout: 'fixed',
              minWidth: '100%',
              // MUI's 16px of side padding costs 32px of every column. On a 38px
              // column that leaves 6px to draw in, which is why device IDs wrapped
              // and the placeholder bars had nowhere to go.
              // The checkbox cell keeps MUI's own padding, or the header label
              // beside it ends up touching the box.
              '& .MuiTableCell-root:not(.MuiTableCell-paddingCheckbox)': {
                  paddingLeft: '8px',
                  paddingRight: '8px',
              },
              // The first label after the checkbox needs a little more room, or it
              // reads as sitting against the box.
              '& .MuiTableCell-paddingCheckbox + .MuiTableCell-root': { paddingLeft: '14px' },
          }
        : { width: '100%', tableLayout: 'fixed', minWidth: 900 };
    const headerCellSx = {
        whiteSpace: 'normal',
        fontSize: isMobile ? '0.78rem' : '0.875rem',
    };
    // Shares of the table, checkbox column aside — it carries MUI's own 48px.
    // The desktop set totals 100 across its nine columns.
    const columnPercents = isMobile
        ? {
              number: '12%',
              status: '18%',
              device: '24%',
              time: '34%',
              trash: '12%',
          }
        : {
              number: '6%',
              status: '9%',
              device: '13%',
              time: '22%',
              count: '12%',
              duration: '11%',
              note: '12%',
              labels: '11%',
              trash: '4%',
          };

    return (
        <TableContainer sx={{ width: '100%', overflowX: isMobile ? 'auto' : 'visible' }}>
            <Table aria-label='records table' size='small' sx={tableSx}>
                <TableHead>
                    <TableRow sx={{ height: ROW_HEIGHT, '& > *': { py: 0.5 } }}>
                        <TableCell padding='checkbox'>
                            <Checkbox
                                size='small'
                                checked={isAllSelected}
                                onChange={onSelectAll}
                                inputProps={{
                                    'aria-label': 'select all records',
                                }}
                            />
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: columnPercents.number }}>{numberLabel}</TableCell>
                        <TableCell align='left' sx={{ ...headerCellSx, width: columnPercents.status }}>
                            {t('status')}
                        </TableCell>
                        <TableCell align='left' sx={{ ...headerCellSx, width: columnPercents.device }}>
                            {deviceLabel}
                        </TableCell>
                        <TableCell align='left' sx={{ ...headerCellSx, width: columnPercents.time }}>
                            {addedTimeLabel}
                        </TableCell>
                        {!isMobile && (
                            <>
                                <TableCell align='left' sx={{ ...headerCellSx, width: columnPercents.count }}>
                                    {t('count')}
                                </TableCell>
                                <TableCell align='left' sx={{ ...headerCellSx, width: columnPercents.duration }}>
                                    {t('duration')}
                                </TableCell>
                                <TableCell align='left' sx={{ ...headerCellSx, width: columnPercents.note }}>
                                    {t('note')}
                                </TableCell>
                                <TableCell align='left' sx={{ ...headerCellSx, width: columnPercents.labels }}>
                                    {t('labels')}
                                </TableCell>
                            </>
                        )}
                        <TableCell align='center' sx={{ ...headerCellSx, width: columnPercents.trash }}>
                            <IconButton size='small' onClick={onDelete} className='text-[#5f6368]'>
                                <Delete className='w-5 h-5 fill-current' />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        Array.from({ length: skeletonRowCount }).map((_: any, index: any) => (
                            <RecordRowSkeleton key={`record-skeleton-${index}`} isMobile={isMobile} />
                        ))
                    ) : isEmpty ? (
                        <TableRow>
                            <TableCell
                                colSpan={columnCount}
                                align='center'
                                sx={{
                                    height: reservedBodyHeight,
                                    borderBottom: 'none',
                                    color: '#757575',
                                    fontSize: isMobile ? '0.82rem' : '0.875rem',
                                }}
                            >
                                {emptyContent ?? t('emptyState')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        paginatedRecords.map((row: any) => {
                            const itemSelected = isSelected(row.id);
                            return (
                                <RecordRow
                                    key={row.deviceId + row.index}
                                    row={row}
                                    isItemSelected={itemSelected}
                                    onClick={onRowClick}
                                    tKey={tKey}
                                    isMobile={isMobile}
                                    mobileDrawerVariant={mobileDrawerVariant}
                                    onRowEdit={onRowEdit}
                                    ro={ro}
                                    labelsById={labelsById}
                                    onLabelMetaUpdated={onLabelMetaUpdated}
                                />
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default RecordsTable;
